import { useEffect, useState } from 'react'

type JsonPageState<T> =
  | { status: 'loading' }
  | { status: 'refreshing'; data: T }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: T }

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'Impossibile caricare la pagina al momento.'
}

export function useJsonPage<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: ReadonlyArray<unknown>,
  options: { keepPreviousData?: boolean } = {},
): JsonPageState<T> {
  const [state, setState] = useState<JsonPageState<T>>({ status: 'loading' })
  const keepPreviousData = options.keepPreviousData

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    setState((current) => {
      if (keepPreviousData && (current.status === 'ready' || current.status === 'refreshing')) {
        return { status: 'refreshing', data: current.data }
      }
      return { status: 'loading' }
    })

    load(controller.signal)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data })
      })
      .catch((error: unknown) => {
        if (!cancelled && !controller.signal.aborted) {
          setState({ status: 'error', message: errorMessage(error) })
        }
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, deps)

  return state
}
